// src/config/database/finishingMaterialsConfig.js

export const finishingMaterialsTableConfig = {
  // Table identification
  id: 'sf_finishing_materials',
  name: 'Small Format Finishing Materials',
  apiEndpoint: '/api/admin/small-format/finishing-materials',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'material_name',
      label: 'Material Name',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'process_name',
      label: 'Finishing Process',
      sortable: true,
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800">
          {value || 'Unknown Process'}
        </span>
      )
    },
    {
      key: 'material_id',
      label: 'Material ID',
      sortable: true,
      render: (value) => (
        <span className="text-gray-600 font-mono text-sm">{value}</span>
      )
    },
    {
      key: 'material_type',
      label: 'Type',
      sortable: true,
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
          {value || 'Unknown'}
        </span>
      )
    },
    {
      key: 'cost',
      label: 'Cost',
      sortable: true,
      render: (value) => (
        <span className="text-green-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(4)}` : '£0.0000'}
        </span>
      )
    },
    {
      key: 'cost_unit',
      label: 'Cost Unit',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-600">
          {value ? value.replace('_', ' ') : 'per item'}
        </span>
      )
    },
    {
      key: 'is_default',
      label: 'Default',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'min_page_count',
      label: 'Min Pages',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">
          {value || 0}
        </span>
      )
    },
    {
      key: 'max_page_count',
      label: 'Max Pages',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">
          {value === 9999 ? '∞' : value || '∞'}
        </span>
      )
    },
    {
      key: 'attribute',
      label: 'Attribute',
      sortable: true,
      render: (value) => (
        <span className="text-gray-600 text-sm truncate max-w-xs" title={value}>
          {value || '-'}
        </span>
      )
    }
  ],

  // Field definitions for the RecordModal form
  fields: [
    {
      key: 'finish_process_id',
      label: 'Finishing Process',
      type: 'select',
      required: true,
      options: [], // Will be populated dynamically
      placeholder: 'Select a finishing process'
    },
    {
      key: 'material_id',
      label: 'Material ID',
      type: 'text',
      required: true,
      placeholder: 'Enter unique material identifier',
      help: 'Must be unique within the selected finishing process'
    },
    {
      key: 'material_name',
      label: 'Material Name',
      type: 'text',
      required: true,
      placeholder: 'Enter descriptive material name'
    },
    {
      key: 'material_type',
      label: 'Material Type',
      type: 'select',
      required: false,
      options: [], // Will be populated dynamically
      placeholder: 'Select or enter material type',
      allowCustom: true
    },
    {
      key: 'cost',
      label: 'Cost (£)',
      type: 'number',
      required: false,
      step: 0.0001,
      min: 0,
      placeholder: '0.0000',
      help: 'Cost per unit as specified in Cost Unit field'
    },
    {
      key: 'cost_unit',
      label: 'Cost Unit',
      type: 'select',
      required: false,
      options: [
        { value: 'per_item', label: 'Per Item' },
        { value: 'per_sheet', label: 'Per Sheet' },
        { value: 'per_cut', label: 'Per Cut' },
        { value: 'per_hour', label: 'Per Hour' },
        { value: 'per_setup', label: 'Per Setup' },
        { value: 'fixed', label: 'Fixed Cost' }
      ],
      defaultValue: 'per_item'
    },
    {
      key: 'attribute',
      label: 'Attribute',
      type: 'text',
      required: false,
      placeholder: 'Optional attribute (e.g., color, size)',
      help: 'Additional attribute for material selection'
    },
    {
      key: 'is_default',
      label: 'Default Selection',
      type: 'checkbox',
      required: false,
      help: 'Whether this material is selected by default for this process'
    },
    {
      key: 'min_page_count',
      label: 'Minimum Page Count',
      type: 'number',
      required: false,
      min: 0,
      defaultValue: 0,
      placeholder: '0',
      help: 'Minimum number of pages for this material to be available'
    },
    {
      key: 'max_page_count',
      label: 'Maximum Page Count',
      type: 'number',
      required: false,
      min: 1,
      defaultValue: 9999,
      placeholder: '9999',
      help: 'Maximum number of pages for this material (9999 = unlimited)'
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Optional description or notes about this material',
      rows: 3
    }
  ]
};

// Transform functions for API communication
export const transformFinishingMaterialForApi = (formData) => {
  return {
    ...formData,
    cost: formData.cost ? parseFloat(formData.cost) : 0,
    is_default: Boolean(formData.is_default),
    min_page_count: formData.min_page_count ? parseInt(formData.min_page_count) : 0,
    max_page_count: formData.max_page_count ? parseInt(formData.max_page_count) : 9999,
    cost_unit: formData.cost_unit || 'per_item'
  };
};

export const transformFinishingMaterialFromApi = (apiData) => {
  return {
    ...apiData,
    cost: apiData.cost ? apiData.cost.toString() : '0',
    is_default: Boolean(apiData.is_default),
    min_page_count: apiData.min_page_count ? apiData.min_page_count.toString() : '0',
    max_page_count: apiData.max_page_count ? apiData.max_page_count.toString() : '9999',
    cost_unit: apiData.cost_unit || 'per_item'
  };
};

// Additional validation rules
export const validateFinishingMaterial = (data) => {
  const errors = {};
  
  if (!data.finish_process_id) {
    errors.finish_process_id = 'Finishing process is required';
  }
  
  if (!data.material_id || data.material_id.trim() === '') {
    errors.material_id = 'Material ID is required';
  }
  
  if (!data.material_name || data.material_name.trim() === '') {
    errors.material_name = 'Material name is required';
  }
  
  if (data.cost && (isNaN(data.cost) || parseFloat(data.cost) < 0)) {
    errors.cost = 'Cost must be a non-negative number';
  }
  
  if (data.min_page_count && (isNaN(data.min_page_count) || parseInt(data.min_page_count) < 0)) {
    errors.min_page_count = 'Minimum page count must be a non-negative number';
  }
  
  if (data.max_page_count && (isNaN(data.max_page_count) || parseInt(data.max_page_count) < 1)) {
    errors.max_page_count = 'Maximum page count must be at least 1';
  }
  
  if (data.min_page_count && data.max_page_count && 
      parseInt(data.min_page_count) > parseInt(data.max_page_count)) {
    errors.max_page_count = 'Maximum page count must be greater than or equal to minimum';
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

export default finishingMaterialsTableConfig;